import { useEffect, useState } from "react";
import { getCommunityAdminPosts, moderateCommunity, featureCommunity, getCommunityReports, resolveReport } from "@/lib/checkout";
import { Loader2, RefreshCw, Eye, EyeOff, Star, Flag, CheckCircle2, RotateCcw } from "lucide-react";
import { AdminGate } from "@/components/AdminGate";
import { useNoIndex } from "@/hooks/useNoIndex";

const REASON_LABELS = {
  spam: "Spam or scam", harassment: "Harassment or bullying", hate: "Hate or discrimination",
  sexual: "Sexual or explicit content", violence: "Violence or threats", self_harm: "Self harm", other: "Something else",
};

export default function AdminCommunity() {
  useNoIndex();
  const [key, setKey] = useState("");
  const [entered, setEntered] = useState(() => localStorage.getItem("cc_admin_key") || "");
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | error | ready
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("posts"); // posts | reports
  const [reports, setReports] = useState([]);
  const [openCount, setOpenCount] = useState(0);

  const load = async (k) => {
    setStatus("loading");
    setError("");
    try {
      const data = await getCommunityAdminPosts(k);
      setPosts(data.posts || []);
      setStatus("ready");
      localStorage.setItem("cc_admin_key", k);
      loadReports(k);
    } catch (e) {
      setStatus("error");
      setError(e.message === "unauthorized" ? "That key is not valid." : "Could not load posts. Try again.");
      if (e.message === "unauthorized") localStorage.removeItem("cc_admin_key");
    }
  };

  const loadReports = async (k) => {
    try {
      const data = await getCommunityReports(k);
      setReports(data.reports || []);
      setOpenCount(data.open_count || 0);
    } catch (e) { /* no-op */ }
  };

  useEffect(() => {
    if (entered) load(entered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setEntered(key);
    load(key);
  };

  const toggle = async (post_id, hidden, comment_index = null) => {
    const busyId = comment_index === null ? post_id : `${post_id}:${comment_index}`;
    setBusy(busyId);
    try {
      await moderateCommunity(entered, { post_id, comment_index, hidden });
      await load(entered);
    } catch (e) { /* no-op */ }
    setBusy("");
  };

  const feature = async (post_id, featured) => {
    setBusy(`feat:${post_id}`);
    try {
      await featureCommunity(entered, post_id, featured);
      await load(entered);
    } catch (e) { /* no-op */ }
    setBusy("");
  };

  const setReportStatus = async (report_id, newStatus) => {
    setBusy(`rep:${report_id}`);
    try {
      await resolveReport(entered, report_id, newStatus);
      await loadReports(entered);
    } catch (e) { /* no-op */ }
    setBusy("");
  };

  const hideReportedPost = async (post_id) => {
    setBusy(`rephide:${post_id}`);
    try {
      await moderateCommunity(entered, { post_id, comment_index: null, hidden: true });
      await load(entered);
    } catch (e) { /* no-op */ }
    setBusy("");
  };

  if (status !== "ready") {
    return (
      <AdminGate
        title="Community moderation"
        subtitle="Enter your admin key to manage posts and comments."
        value={key}
        onChange={(e) => setKey(e.target.value)}
        onSubmit={submit}
        loading={status === "loading"}
        error={error}
        gateTestId="admin-community-gate"
        inputTestId="admin-community-key-input"
        errorTestId="admin-community-error"
        buttonTestId="admin-community-unlock"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0B132B] px-6 py-12" data-testid="admin-community-dashboard">
      <div className="mx-auto max-w-[760px]">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-extrabold text-white">Community moderation</h1>
          <button type="button" onClick={() => load(entered)} data-testid="admin-community-refresh"
            className="inline-flex items-center gap-2 rounded-xl border border-[#2A3A5E] px-4 py-2 text-sm font-semibold text-[#A8D5F5] hover:bg-white/5">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
        <p className="mt-2 text-sm text-[#7BA8C4]">Hidden items disappear from the public feed. You can unhide anytime.</p>

        <div className="mt-6 flex gap-2" data-testid="admin-community-tabs">
          <button type="button" onClick={() => setTab("posts")} data-testid="admin-tab-posts"
            className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors ${tab === "posts" ? "bg-[#15816B] text-white" : "border border-[#2A3A5E] text-[#A8D5F5] hover:bg-white/5"}`}>
            Posts
          </button>
          <button type="button" onClick={() => { setTab("reports"); loadReports(entered); }} data-testid="admin-tab-reports"
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-colors ${tab === "reports" ? "bg-[#15816B] text-white" : "border border-[#2A3A5E] text-[#A8D5F5] hover:bg-white/5"}`}>
            <Flag className="h-4 w-4" /> Reports
            {openCount > 0 && <span className="rounded-full bg-[#C0392B] px-2 py-0.5 text-xs text-white" data-testid="admin-reports-badge">{openCount}</span>}
          </button>
        </div>

        {tab === "posts" && (
        <div className="mt-8 space-y-4">
          {posts.length === 0 && <p className="text-[#7BA8C4]">No posts yet.</p>}
          {posts.map((p) => (
            <div key={p.id} data-testid={`admin-post-${p.id}`}
              className={`rounded-2xl border p-5 ${p.hidden ? "border-[#5A3A3A] bg-[#1A1414]" : "border-[#1E2A47] bg-[#111A30]"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-heading font-bold text-white">
                    {p.name} {p.pinned && <span className="ml-2 text-xs text-[#F5A623]">Pinned</span>}
                    {p.featured && <span className="ml-2 text-xs text-[#15816B]">Featured</span>}
                    {p.hidden && <span className="ml-2 rounded bg-[#5A3A3A] px-2 py-0.5 text-xs text-[#FFC7C7]">Hidden</span>}
                  </p>
                  <p className="mt-0.5 text-xs text-[#7BA8C4]">{p.tag} &middot; {p.time}</p>
                  <p className="mt-2 whitespace-pre-wrap text-[15px] text-[#D6E2F0]">{p.body}</p>
                </div>
                <div className="flex flex-shrink-0 flex-col gap-2">
                  <button type="button" onClick={() => feature(p.id, !p.featured)} disabled={busy === `feat:${p.id}`}
                    data-testid={`admin-feature-post-${p.id}`}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                      p.featured ? "bg-[#15816B] text-white hover:bg-[#126f5c]" : "border border-[#3A4A6E] text-[#A8D5F5] hover:bg-white/5"
                    }`}>
                    {busy === `feat:${p.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Star className="h-4 w-4" /> {p.featured ? "Unfeature" : "Feature"}</>}
                  </button>
                  <button type="button" onClick={() => toggle(p.id, !p.hidden)} disabled={busy === p.id}
                    data-testid={`admin-toggle-post-${p.id}`}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
                      p.hidden ? "bg-[#15816B] text-white hover:bg-[#126f5c]" : "border border-[#3A4A6E] text-[#A8D5F5] hover:bg-white/5"
                    }`}>
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : p.hidden ? <><Eye className="h-4 w-4" /> Unhide</> : <><EyeOff className="h-4 w-4" /> Hide</>}
                  </button>
                </div>
              </div>

              {p.comments.length > 0 && (
                <div className="mt-4 space-y-2 border-t border-white/10 pt-3">
                  {p.comments.map((c) => (
                    <div key={c.index} className="flex items-start justify-between gap-3" data-testid={`admin-comment-${p.id}-${c.index}`}>
                      <p className={`text-[13px] ${c.hidden ? "text-[#7A6A6A] line-through" : "text-[#B9C7D8]"}`}>
                        <span className="font-semibold text-[#8FB0CC]">{c.name}: </span>{c.body}
                      </p>
                      <button type="button" onClick={() => toggle(p.id, !c.hidden, c.index)} disabled={busy === `${p.id}:${c.index}`}
                        data-testid={`admin-toggle-comment-${p.id}-${c.index}`}
                        className="flex-shrink-0 text-xs font-semibold text-[#A8D5F5] hover:text-white">
                        {busy === `${p.id}:${c.index}` ? "..." : c.hidden ? "Unhide" : "Hide"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {tab === "reports" && (
        <div className="mt-8 space-y-4" data-testid="admin-reports-list">
          {reports.length === 0 && <p className="text-[#7BA8C4]" data-testid="admin-reports-empty">No reports yet. When a member reports a post, comment, or message, it shows up here.</p>}
          {reports.map((r) => (
            <div key={r.id} data-testid={`admin-report-${r.id}`}
              className={`rounded-2xl border p-5 ${r.status === "open" ? "border-[#5A4A2A] bg-[#17140B]" : "border-[#1E2A47] bg-[#111A30]"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-heading font-bold text-white">
                    {REASON_LABELS[r.reason] || r.reason}
                    <span className="ml-2 rounded px-2 py-0.5 text-xs font-semibold capitalize"
                      style={{ background: r.status === "open" ? "#5A4A2A" : "#1E3A2E", color: r.status === "open" ? "#F5D79A" : "#9FE3C8" }}>
                      {r.status}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-[#7BA8C4]">
                    Reported by {r.reporter_name} &middot; {r.time} &middot; {r.target_type}
                  </p>
                  <p className="mt-2 text-[13px] text-[#8FB0CC]">Author: <span className="font-semibold text-[#B9C7D8]">{r.target_author_name}</span></p>
                  {r.excerpt && <p className="mt-2 whitespace-pre-wrap rounded-lg bg-black/20 px-3 py-2 text-[14px] text-[#D6E2F0]">{r.excerpt}</p>}
                  {r.note && <p className="mt-2 text-[13px] italic text-[#A8D5F5]">Note: {r.note}</p>}
                </div>
                <div className="flex flex-shrink-0 flex-col gap-2">
                  {r.post_id && (
                    <button type="button" onClick={() => hideReportedPost(r.post_id)} disabled={busy === `rephide:${r.post_id}`}
                      data-testid={`admin-report-hide-${r.id}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#5A3A3A] px-3 py-2 text-xs font-bold text-[#FFC7C7] hover:bg-[#2A1818]">
                      {busy === `rephide:${r.post_id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><EyeOff className="h-4 w-4" /> Hide post</>}
                    </button>
                  )}
                  {r.status === "open" ? (
                    <button type="button" onClick={() => setReportStatus(r.id, "resolved")} disabled={busy === `rep:${r.id}`}
                      data-testid={`admin-report-resolve-${r.id}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#15816B] px-3 py-2 text-xs font-bold text-white hover:bg-[#126f5c]">
                      {busy === `rep:${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" /> Mark resolved</>}
                    </button>
                  ) : (
                    <button type="button" onClick={() => setReportStatus(r.id, "open")} disabled={busy === `rep:${r.id}`}
                      data-testid={`admin-report-reopen-${r.id}`}
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#3A4A6E] px-3 py-2 text-xs font-bold text-[#A8D5F5] hover:bg-white/5">
                      {busy === `rep:${r.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="h-4 w-4" /> Reopen</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}
